import React, { useCallback, useEffect, useRef, useState } from "react";
import { Beer, Plus, Save, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, barImageUrl } from "@/lib/api";
import PageLogoManager from "@/components/PageLogoManager";

function emptyProduct() {
  return { name: "", description: "", category: "", price: 0, cost: 0, stock: "", active: true };
}

export default function BarManager() {
  const [products, setProducts] = useState([]);
  const [draft, setDraft] = useState(emptyProduct());
  const [pix, setPix] = useState({ pix_key: "", pix_holder: "" });
  const imgRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/bar/products");
      setProducts(res.data);
      const s = await api.get("/settings");
      setPix({ pix_key: s.data.bar_pix_key || "", pix_holder: s.data.bar_pix_holder || "" });
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addProduct = async () => {
    if (!draft.name.trim()) return toast.error("Informe o nome do produto.");
    try {
      await api.post("/admin/bar/products", { ...draft, price: Number(draft.price) || 0, cost: Number(draft.cost) || 0, stock: draft.stock === "" ? null : Number(draft.stock) });
      setDraft(emptyProduct());
      await load();
      toast.success("Produto adicionado!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setField = (id, field, value) => setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const saveProduct = async (p) => {
    try {
      await api.patch(`/admin/bar/products/${p.id}`, {
        name: p.name, description: p.description || "", category: p.category || "",
        price: Number(p.price) || 0, cost: Number(p.cost) || 0, active: !!p.active,
        stock: p.stock === "" || p.stock === null || p.stock === undefined ? null : Number(p.stock),
      });
      toast.success("Produto salvo.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delProduct = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    try { await api.delete(`/admin/bar/products/${id}`); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const next = [...products];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setProducts(next);
    try { await api.post("/admin/bar/products/reorder", { ids: next.map((p) => p.id) }); } catch (e) { /* ignore */ }
  };

  const uploadImg = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/bar/products/${id}/image`, fd); await load(); toast.success("Imagem enviada!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePix = async () => {
    try { await api.post("/admin/settings/bar-pix", pix); toast.success("PIX do bar atualizado!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8" data-testid="bar-manager">
      <div className="flex items-center gap-2">
        <Beer className="h-5 w-5 text-[#C7962B]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Bar — Produtos</h2>
      </div>

      {/* PIX */}
      <div className="mt-3 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input data-testid="barpix-key" placeholder="Chave PIX" value={pix.pix_key} onChange={(e) => setPix({ ...pix, pix_key: e.target.value })} className="rounded-xl" />
        <Input data-testid="barpix-holder" placeholder="Titular" value={pix.pix_holder} onChange={(e) => setPix({ ...pix, pix_holder: e.target.value })} className="rounded-xl" />
        <Button data-testid="barpix-save" onClick={savePix} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337]"><Save className="mr-1.5 h-4 w-4" /> Salvar PIX</Button>
      </div>

      {/* Add product */}
      <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-7">
        <Input data-testid="newprod-name" placeholder="Nome" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="rounded-xl sm:col-span-2" />
        <Input data-testid="newprod-category" placeholder="Categoria" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="rounded-xl" />
        <Input data-testid="newprod-price" type="number" placeholder="Venda R$" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} className="rounded-xl" />
        <Input data-testid="newprod-cost" type="number" placeholder="Custo R$" value={draft.cost} onChange={(e) => setDraft({ ...draft, cost: e.target.value })} className="rounded-xl" />
        <Input data-testid="newprod-stock" type="number" placeholder="Estoque" value={draft.stock} onChange={(e) => setDraft({ ...draft, stock: e.target.value })} className="rounded-xl" />
        <Button data-testid="newprod-add" onClick={addProduct} className="rounded-xl bg-[#E8611A] text-white hover:bg-[#CF5212]"><Plus className="h-4 w-4" /></Button>
      </div>

      {/* Product list */}
      <div className="mt-4 space-y-2">
        {products.map((p, idx) => (
          <div key={p.id} data-testid={`prod-row-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <button data-testid={`prod-up-${p.id}`} onClick={() => move(idx, -1)} className="rounded bg-slate-100 p-1 hover:bg-slate-200"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button data-testid={`prod-down-${p.id}`} onClick={() => move(idx, 1)} className="rounded bg-slate-100 p-1 hover:bg-slate-200"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                {barImageUrl(p) ? <img src={barImageUrl(p)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-5 w-5 text-slate-300" /></div>}
              </div>
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-6">
                <Input value={p.name} onChange={(e) => setField(p.id, "name", e.target.value)} className="rounded-lg sm:col-span-2" />
                <Input value={p.category || ""} placeholder="Categoria" onChange={(e) => setField(p.id, "category", e.target.value)} className="rounded-lg" />
                <label className="text-[10px] font-semibold uppercase text-slate-400">Venda<Input type="number" value={p.price} onChange={(e) => setField(p.id, "price", e.target.value)} className="rounded-lg" /></label>
                <label className="text-[10px] font-semibold uppercase text-slate-400">Custo<Input type="number" value={p.cost} onChange={(e) => setField(p.id, "cost", e.target.value)} className="rounded-lg" /></label>
                <label className="text-[10px] font-semibold uppercase text-slate-400">Estoque<Input data-testid={`prod-stock-${p.id}`} type="number" placeholder="∞" value={p.stock ?? ""} onChange={(e) => setField(p.id, "stock", e.target.value)} className="rounded-lg" /></label>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <input type="checkbox" checked={!!p.active} onChange={(e) => setField(p.id, "active", e.target.checked)} /> Ativo (visível no cardápio)
              </label>
              <input ref={(el) => (imgRefs.current[p.id] = el)} type="file" accept="image/*" onChange={(e) => uploadImg(p.id, e)} className="hidden" data-testid={`prod-img-input-${p.id}`} />
              <Button data-testid={`prod-img-${p.id}`} onClick={() => imgRefs.current[p.id]?.click()} size="sm" variant="outline" className="rounded-full border-slate-200"><Upload className="mr-1 h-3.5 w-3.5" /> Foto</Button>
              <Button data-testid={`prod-save-${p.id}`} onClick={() => saveProduct(p)} size="sm" className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"><Save className="mr-1 h-3.5 w-3.5" /> Salvar</Button>
              <Button data-testid={`prod-del-${p.id}`} onClick={() => delProduct(p.id)} size="sm" variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <PageLogoManager
        title="Logo do Bar"
        flagKey="bar_logo"
        updatedKey="bar_logo_updated_at"
        serveUrl="/settings/bar-logo"
        uploadUrl="/admin/bar-logo"
        testid="bar-logo"
      />
    </div>
  );
}
